import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import type { AdminSettings as AdminSettingsDto } from '@/api/generated/types.gen';

export const Route = createFileRoute('/admin/settings')({
  component: AdminSettings,
});

function AdminSettings() {
  const settings = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api<AdminSettingsDto>('/admin/settings'),
    retry: false,
  });

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Reviewer console"
        title="Settings"
        description={
          <>
            Practice-wide configuration, sourced from the running backend. Values are read-only —
            change them via{' '}
            <code className="rounded-(--radius-sm) bg-(--color-paper-2) px-1 font-mono text-[12px]">
              TaxYearConfigs
            </code>{' '}
            and{' '}
            <code className="rounded-(--radius-sm) bg-(--color-paper-2) px-1 font-mono text-[12px]">
              AgentOptions
            </code>
            .
          </>
        }
      />

      {settings.isLoading ? (
        <p className="text-[13px] text-(--color-ink-muted)">Loading settings…</p>
      ) : settings.isError ? (
        <p className="text-[13px] text-(--color-ink-muted)">Could not load settings.</p>
      ) : settings.data ? (
        <>
          <Card>
            <CardContent className="space-y-3 p-7">
              <div>
                <div className="section-ref">Tax year</div>
                <h2 className="mt-1 font-serif text-xl font-medium">
                  FY {settings.data.taxYear.financialYear} constants
                </h2>
              </div>
              <dl className="divide-y divide-(--color-rule) border-t border-(--color-rule) text-[14px]">
                <Row k="Active financial year" v={settings.data.taxYear.financialYear ?? ''} />
                <Row k="FY end year" v={String(settings.data.taxYear.fyEndYear)} />
                <Row
                  k="Tax-free threshold"
                  v={formatAud(Number(settings.data.taxYear.taxFreeThreshold))}
                />
                <Row
                  k="Top marginal rate"
                  v={formatPct(Number(settings.data.taxYear.topMarginalRate))}
                />
                <Row k="LITO maximum" v={formatAud(Number(settings.data.taxYear.litoMax))} />
                <Row
                  k="Medicare levy"
                  v={formatPct(Number(settings.data.taxYear.medicareRate))}
                />
                <Row
                  k="WFH fixed rate"
                  v={`${formatAud(Number(settings.data.taxYear.wfhRatePerHour), 2)} / hour`}
                />
                <Row
                  k="Car cents/km"
                  v={`${formatAud(Number(settings.data.taxYear.carCentsPerKm), 2)} / km (capped ${Number(settings.data.taxYear.carMaxKm).toLocaleString('en-AU')} km)`}
                />
                <Row
                  k="Concessional super cap"
                  v={formatAud(Number(settings.data.taxYear.concessionalSuperCap))}
                />
                <Row
                  k="Super guarantee rate"
                  v={formatPct(Number(settings.data.taxYear.superGuaranteeRate))}
                />
                <Row k="Pension age" v={String(settings.data.taxYear.pensionAge)} />
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-7">
              <div>
                <div className="section-ref">Agent</div>
                <h2 className="mt-1 font-serif text-xl font-medium">NightAI assistant</h2>
              </div>
              <dl className="divide-y divide-(--color-rule) border-t border-(--color-rule) text-[14px]">
                <Row k="Provider" v={settings.data.agent.provider ?? ''} />
                <Row k="Model" v={settings.data.agent.model ?? ''} />
                <Row k="Temperature" v={String(settings.data.agent.temperature)} />
                <Row k="Max tokens" v={String(settings.data.agent.maxTokens)} />
                <Row
                  k="Max tool calls / turn"
                  v={String(settings.data.agent.maxToolCallsPerTurn)}
                />
                <Row k="System prompt path" v={settings.data.agent.systemPromptPath ?? ''} />
                <Row
                  k="Prompt caching"
                  v={settings.data.agent.promptCachingEnabled ? 'Enabled' : 'Disabled'}
                />
              </dl>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <dt className="text-(--color-ink-muted)">{k}</dt>
      <dd className="font-mono text-right tabular-nums text-(--color-ink)">{v}</dd>
    </div>
  );
}

function formatAud(value: number, fractionDigits = 0): string {
  return value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatPct(value: number): string {
  return `${(value * 100).toFixed(value < 0.1 ? 1 : 0)}%`;
}
